'use client';

import { useLockBodyScroll } from '@jouzen/ecom-components/hooks/useLockBodyScroll';
import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { cx } from 'class-variance-authority';
import { useCallback, useState } from 'react';

import { EventType } from '@/analytics/types';
import { useDecagonContext } from '@/app/contexts/DecagonProvider';

import Motion from '../Motion';
import ChatbotTriggerButton from './ChatbotTriggerButton';
import ChatbotWindow from './ChatbotWindow';

export interface ChatbotProps {
  readonly className?: string;
  readonly chatClosedClassName?: string;
  readonly chatOpenClassName?: string;
  readonly shouldHideTriggerButton?: boolean;
  readonly initialMessage?: string;
}

const Chatbot = ({
  className,
  chatClosedClassName,
  chatOpenClassName,
  initialMessage,
  shouldHideTriggerButton,
}: ChatbotProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isDecagonScriptLoaded } = useDecagonContext();
  const isMobile = !useMediaQuery(`(min-width: ${BREAKPOINTS.medium}px)`);
  useLockBodyScroll(isOpen && isMobile);

  const onTriggerClick = () => {
    setIsOpen(true);
    void window.ouraAnalytics.track(EventType.ModuleClicked, {
      action: 'open chatbot',
      location: 'bubble',
    });
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Motion
      animate={isOpen ? 'chatWindowVariant' : 'buttonVariant'}
      className={cx(
        'md:rounded-xl',
        `[--chat-window-height:100dvh] md:[--chat-window-height:min(85dvh,780px)]`,
        `[--chat-window-width:100dvw] md:[--chat-window-width:min(85dvw,420px)]`,
        className,
        isOpen ? chatOpenClassName : chatClosedClassName,
      )}
      initial="buttonVariant"
      transition={{ type: 'spring', duration: 0.3, bounce: 0.25 }}
      variants={{
        buttonVariant: {
          height: 'auto',
          width: 'auto',
        },
        chatWindowVariant: {
          height: `var(--chat-window-height)`,
          width: `var(--chat-window-width)`,
          overflow: 'hidden',
        },
      }}
    >
      <div
        className={cx(
          'flex h-full w-full items-center justify-center bg-white shadow-[-5px_5px_20px_rgba(66,66,66,0.25)]',
          isOpen ? 'md:rounded-xl' : 'rounded-full',
          { hidden: !isDecagonScriptLoaded },
        )}
        data-cy="chatbot"
      >
        <Motion
          animate={shouldHideTriggerButton ? 'hiddenVariant' : 'visibleVariant'}
          initial={shouldHideTriggerButton ? 'hiddenVariant' : 'visibleVariant'}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          variants={{
            visibleVariant: { opacity: 1, display: 'flex' },
            hiddenVariant: { opacity: 0, display: 'none' },
          }}
        >
          <ChatbotTriggerButton
            className={cx({ hidden: isOpen })}
            onClick={onTriggerClick}
          />
        </Motion>
        <ChatbotWindow
          className={cx({ hidden: !isOpen })}
          initialMessage={initialMessage}
          onClose={onClose}
        />
      </div>
    </Motion>
  );
};

export default Chatbot;
